import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Col,
  Form,
  Select,
  Button,
  Table,
  message,
  Row,
  DatePicker,
  Space,
  Input,
  Spin,
  Empty,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import API from "../../api";
import moment from "moment";
import { debounce } from "lodash";
import { useSelector } from "react-redux";

const { Option } = Select;

const StockMovementForm = () => {
  const [stores, setStores] = useState([]);
  const [user, setUser] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [productTable, setProductTable] = useState([]);
  const [selectedToStoreId, setSelectedToStoreId] = useState(null);
  const [transferDate, setTransferDate] = useState(null);
  const [isProductLoading, setIsProductLoading] = useState(false);
  const [isStoreLoading, setIsStoreLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [stocks, setStocks] = useState([]);
  const [trackingNumbers, setTrackingNumbers] = useState([]);
  const [batchNumbers, setBatchNumbers] = useState([]);
  const [selectedTrackingNo, setSelectedTrackingNo] = useState(null);
  const [selectedBatchNo, setSelectedBatchNo] = useState(null);
  const [storeSettings, setStoreSettings] = useState({
    trackingNumber: "No",
    batchNumber: "No",
  });

  const productSettings = useSelector(
    (state) => state.productSetting?.productSetting
  );

  // Fetch stores data
  const fetchData = async () => {
    setIsStoreLoading(true);
    try {
      const storeResponse = await API.get("/stores");
      const fetchedStores = Array.isArray(storeResponse?.data)
        ? storeResponse.data
        : [];
      setStores(fetchedStores);
    } catch (error) {
      console.error("Error fetching stores:", error);
      message.error("Failed to fetch stores.");
    } finally {
      setIsStoreLoading(false);
    }
  };

  // Fetch tracking numbers and batch numbers for selected product
  const fetchTrackingAndBatchNumbers = useCallback(() => {
    if (!selectedProduct || !selectedStore?._id || !Array.isArray(stocks)) {
      setTrackingNumbers([]);
      setBatchNumbers([]);
      setSelectedTrackingNo(null);
      setSelectedBatchNo(null);
      return;
    }

    const stock = stocks.find(
      (s) =>
        s.storeId?._id === selectedStore._id &&
        s.productId?._id === selectedProduct.product.id
    );

    if (!stock) {
      setTrackingNumbers([]);
      setBatchNumbers([]);
      setSelectedTrackingNo(null);
      setSelectedBatchNo(null);
      return;
    }

    if (Array.isArray(stock.batch) && stock.batch.length > 0) {
      const trackingNumbers = [
        ...new Set(
          stock.batch.map((batch) => batch.tracking_number).filter(Boolean)
        ),
      ];
      const batchNumbers = [
        ...new Set(
          stock.batch.map((batch) => batch.batchNumber).filter(Boolean)
        ),
      ];
      setTrackingNumbers(trackingNumbers);
      setBatchNumbers(batchNumbers);
    } else {
      setTrackingNumbers([]);
      setBatchNumbers([]);
      setSelectedTrackingNo(null);
      setSelectedBatchNo(null);
    }
  }, [selectedProduct, selectedStore, stocks]);

  // Fetch products with fallback to /products/paginate
  const fetchProducts = useCallback(
    async (search = "", storeId = selectedStore?._id) => {
      if (!storeId) {
        console.warn("storeId is required to fetch products.");
        setProducts([]);
        return;
      }

      setIsProductLoading(true);
      try {
        const params = {
          search: search.trim(),
          storeId,
          page: currentPage,
          limit: pageLimit,
        };

        const res = await API.get("/stocks", { params });
        const stocks = Array.isArray(res?.data?.data?.stocks)
          ? res.data.data.stocks
          : [];
        setStocks(stocks);

        const param = {
          search: search.trim(),
          storeId,
        };

        const response = await API.get("/products/paginate", { params: param });
        const products = Array.isArray(response?.data?.products)
          ? response.data.products
          : [];

        const productsMap = new Map(products.map((prod) => [prod._id, prod]));
        const productMap = new Map();

        stocks.forEach((stock) => {
          const product = stock.productId;
          if (!product?._id) {
            console.warn("Invalid product data in stock:", stock);
            return;
          }
          const productId = product._id;
          const productDetails = productsMap.get(productId);

          if (!productMap.has(productId)) {
            productMap.set(productId, {
              stockId: stock._id,
              storeId: stock.storeId?._id || stock.storeId,
              storeName: stock.storeId?.name || "",
              product: {
                id: productId,
                name: product.specifications?.name || "Unnamed Product",
              },
              barcode: product.barcode?.code || "",
              hsn_sac: product.hsn_sac || "",
              quantity: stock.quantity || 0,
              batchId: null,
              batchNumber: "",
              trackingNumber: "",
              purchasePrice: 0,
              salePrice: 0,
              mrp: 0,
              taxes: {
                id: "",
                percentage: 0,
              },
              batches:
                stock.batch && Array.isArray(stock.batch) ? stock.batch : [],
              productPricing: productDetails?.pricing || null,
            });
          } else {
            const existingProduct = productMap.get(productId);
            existingProduct.quantity += stock.quantity || 0;
            if (stock.batch && Array.isArray(stock.batch)) {
              existingProduct.batches.push(...stock.batch);
            }
          }
        });

        const enrichedProducts = Array.from(productMap.values()).map(
          (product) => {
            const firstBatch =
              product.batches.length > 0 ? product.batches[0] : null;
            const fallbackPricing = product.productPricing || {};
            const mrp = firstBatch?.mrp || fallbackPricing.mrpPrice || 0;
            const salePrice =
              firstBatch?.sellingPrice || fallbackPricing.salePrice || 0;
            const purchasePrice =
              firstBatch?.purchasePrice || fallbackPricing.purchasePrice || 0;
            const taxes = firstBatch?.gst
              ? {
                  id: firstBatch.gst.id || "",
                  percentage: firstBatch.gst.percentage || 0,
                }
              : fallbackPricing.taxes &&
                Array.isArray(fallbackPricing.taxes) &&
                fallbackPricing.taxes.length > 0
              ? {
                  id: fallbackPricing.taxes[0].taxId || "",
                  percentage: fallbackPricing.taxes[0].percentage || 0,
                }
              : { id: "", percentage: 0 };

            return {
              ...product,
              batchId: firstBatch?._id || null,
              batchNumber: firstBatch?.batchNumber || "",
              trackingNumber: firstBatch?.tracking_number || "",
              purchasePrice,
              salePrice,
              mrp,
              taxes,
            };
          }
        );

        setProducts(enrichedProducts);
      } catch (error) {
        console.error("Error fetching products:", error);
        message.error("Failed to fetch products.");
        setProducts([]);
      } finally {
        setIsProductLoading(false);
      }
    },
    [selectedStore, currentPage, pageLimit]
  );

  const debouncedFetch = useRef(
    debounce((search, storeId) => {
      setIsProductLoading(true);
      fetchProducts(search, storeId).finally(() => setIsProductLoading(false));
    }, 500)
  ).current;

  useEffect(() => {
    const initialize = async () => {
      await fetchData();
      const userData = JSON.parse(localStorage?.getItem("userData"));
      setUser(userData);
    };
    initialize();
  }, []);

  useEffect(() => {
    if (user?.role !== "admin" && stores.length > 0) {
      const defaultStore = stores.find((store) => store._id === user.storeId);
      setSelectedStore(defaultStore || null);
    }
  }, [user, stores]);

  useEffect(() => {
    if (selectedStore) {
      fetchProducts("", selectedStore?._id);
    } else {
      setProducts([]);
      setSelectedProduct(null);
      setTrackingNumbers([]);
      setBatchNumbers([]);
      setSelectedTrackingNo(null);
      setSelectedBatchNo(null);
    }
  }, [selectedStore, fetchProducts]);

  useEffect(() => {
    fetchTrackingAndBatchNumbers();
  }, [fetchTrackingAndBatchNumbers]);

  useEffect(() => {
    const userData = JSON.parse(localStorage?.getItem("userData"));
    setUser(userData);

    if (userData) {
      const selectedStoreId = selectedStore?._id;
      if (userData.role === "admin") {
        const settings = productSettings.find(
          (setting) => setting.storeId === selectedStoreId
        );
        if (settings) {
          setStoreSettings({
            trackingNumber: settings?.engineNumber || "No",
            batchNumber: settings?.batchNumber || "No",
          });
        } else {
          setStoreSettings({ trackingNumber: "No", batchNumber: "No" });
        }
      } else if (userData.storeId) {
        const settings = productSettings.find(
          (setting) => setting.storeId === userData.storeId
        );

        if (settings) {
          setStoreSettings({
            trackingNumber: settings?.engineNumber || "No",
            batchNumber: settings?.batchNumber || "No",
          });
        } else {
          setStoreSettings({ trackingNumber: "No", batchNumber: "No" });
        }
      }
    }
  }, [selectedStore?._id, productSettings]);

  const formatNumber = (number) => `₹${new Intl.NumberFormat().format(number)}`;

  const handleAddProduct = () => {
    if (!selectedProduct || quantity <= 0) {
      message.error("Please select a valid product and quantity.");
      return;
    }

    // Validate quantity against batch if trackingNo or batchNo is provided
    let availableQuantity = selectedProduct.quantity || 0;
    if (selectedTrackingNo || selectedBatchNo) {
      const stock = stocks.find(
        (s) =>
          s.storeId?._id === selectedStore._id &&
          s.productId?._id === selectedProduct.product.id
      );
      const batch = stock?.batch?.find(
        (b) =>
          (!selectedTrackingNo || b.tracking_number === selectedTrackingNo) &&
          (!selectedBatchNo || b.batchNumber === selectedBatchNo)
      );
      if (batch) {
        availableQuantity = batch.quantity || 0;
      }
    }

    if (quantity > availableQuantity) {
      message.warning(
        `Insufficient stock for ${selectedProduct.product.name}${
          selectedTrackingNo ? ` (Tracking: ${selectedTrackingNo})` : ""
        }${
          selectedBatchNo ? ` (Batch: ${selectedBatchNo})` : ""
        }. Only ${availableQuantity} items available.`
      );
      return;
    }

    setProductTable((prevTable) => {
      const existingProduct = prevTable.find(
        (item) =>
          item.productId === selectedProduct.product.id &&
          item.trackingNo === selectedTrackingNo &&
          item.batchNo === selectedBatchNo
      );
      let updatedTable;
      if (existingProduct) {
        updatedTable = prevTable.map((item) =>
          item.productId === selectedProduct.product.id &&
          item.trackingNo === selectedTrackingNo &&
          item.batchNo === selectedBatchNo
            ? {
                ...item,
                quantity: Math.min(item.quantity + quantity, availableQuantity),
              }
            : item
        );
      } else {
        const batch =
          selectedTrackingNo || selectedBatchNo
            ? stocks
                .find(
                  (s) =>
                    s.storeId?._id === selectedStore._id &&
                    s.productId?._id === selectedProduct.product.id
                )
                ?.batch?.find(
                  (b) =>
                    (!selectedTrackingNo ||
                      b.tracking_number === selectedTrackingNo) &&
                    (!selectedBatchNo || b.batchNumber === selectedBatchNo)
                )
            : null;
        const newProduct = {
          key: `${selectedProduct.product.id}-${
            selectedTrackingNo || "no-tracking"
          }-${selectedBatchNo || "no-batch"}`,
          productId: selectedProduct.product.id,
          name: selectedProduct.product.name,
          quantity: Math.min(quantity, availableQuantity),
          maxQuantity: availableQuantity,
          pricing: {
            mrpPrice: batch?.mrp || selectedProduct.mrp,
            purchasePrice:
              batch?.purchasePrice || selectedProduct.purchasePrice,
            salePrice: batch?.sellingPrice || selectedProduct.salePrice,
          },
          margin: 0,
          trackingNo: selectedTrackingNo || null,
          batchNo: selectedBatchNo || null,
        };
        updatedTable = [...prevTable, newProduct];
      }

      setSelectedProduct(null);
      setSelectedTrackingNo(null);
      setSelectedBatchNo(null);
      setQuantity(1);
      return updatedTable;
    });
  };

  const handleRemoveProduct = (key) => {
    setProductTable((prevTable) =>
      prevTable.filter((item) => item.key !== key)
    );
  };

  const handleSubmitTransfer = async () => {
    if (
      !selectedStore ||
      !selectedToStoreId ||
      productTable.length === 0 ||
      !transferDate
    ) {
      message.error(
        "Please select stores, add products, and specify a transfer date."
      );
      return;
    }
    if (!user?.id) {
      message.error("User not authenticated. Please log in.");
      return;
    }

    setIsSubmitting(true);
    try {
      const transferData = {
        sourceStoreId: selectedStore?._id,
        destinationStoreId: selectedToStoreId,
        transferrerId: user.id,
        transferDate: transferDate.toISOString(),
        products: productTable.map((product) => ({
          productId: product.productId,
          quantity: product.quantity,
          margin: {
            marginValue: product.margin || 0,
            storeId: selectedToStoreId,
          },
          trackingNo: product.trackingNo || null,
          batchNo: product.batchNo || null,
        })),
      };
      const response = await API.post(
        "/stock-movements/transfer",
        transferData
      );
      message.success(
        `Stock transferred successfully! Invoice No: ${response.data.invoiceNo}`
      );
      setProductTable([]);
      setSelectedStore(null);
      setSelectedToStoreId(null);
      setTransferDate(null);
      setSelectedProduct(null);
      setSelectedTrackingNo(null);
      setSelectedBatchNo(null);
      fetchData();
    } catch (error) {
      console.error("Error transferring stock:", error);
      message.error("Failed to transfer stock.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarginChange = (key, newMargin) => {
    setProductTable((prevTable) =>
      prevTable.map((item) =>
        item.key === key
          ? { ...item, margin: Math.max(0, Number(newMargin) || 0) }
          : item
      )
    );
  };

  const columns = [
    { title: "Name", key: "name", dataIndex: "name" },

    ...(storeSettings.trackingNumber === "Yes"
      ? [
          {
            title: "Tracking No",
            key: "trackingNo",
            dataIndex: "trackingNo",
            render: (trackingNo) => trackingNo || "N/A",
          },
        ]
      : []),

    ...(storeSettings.batchNumber === "Yes"
      ? [
          {
            title: "Batch No",
            key: "batchNo",
            dataIndex: "batchNo",
            render: (batchNo) => batchNo || "N/A",
          },
        ]
      : []),

    {
      title: "MRP",
      key: ["pricing", "mrpPrice"],
      dataIndex: ["pricing", "mrpPrice"],
      render: formatNumber,
    },
    {
      title: "Purchase Price",
      key: ["pricing", "purchasePrice"],
      dataIndex: ["pricing", "purchasePrice"],
      render: formatNumber,
    },
    {
      title: "Sale Price",
      key: ["pricing", "salePrice"],
      dataIndex: ["pricing", "salePrice"],
      render: formatNumber,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      render: (text, record) => (
        <Space>
          <Button
            type="link"
            onClick={() =>
              setProductTable((prevTable) =>
                prevTable.map((item) =>
                  item.key === record.key && item.quantity > 0
                    ? { ...item, quantity: item.quantity - 1 }
                    : item
                )
              )
            }
            disabled={isSubmitting}
          >
            -
          </Button>
          {record.quantity}
          <Button
            type="link"
            onClick={() =>
              setProductTable((prevTable) =>
                prevTable.map((item) =>
                  item.key === record.key
                    ? {
                        ...item,
                        quantity: Math.min(item.quantity + 1, item.maxQuantity),
                      }
                    : item
                )
              )
            }
            disabled={record.quantity >= record.maxQuantity || isSubmitting}
          >
            +
          </Button>
          <span>(Max: {record.maxQuantity})</span>
        </Space>
      ),
    },
    {
      title: "Margin (%)",
      dataIndex: "margin",
      key: "margin",
      render: (text, record) => (
        <Input
          type="number"
          min={0}
          value={record.margin}
          onChange={(e) => handleMarginChange(record.key, e.target.value)}
          style={{ width: "100px" }}
          disabled={isSubmitting}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveProduct(record.key)}
          disabled={isSubmitting}
        />
      ),
    },
  ];

  return (
    <div
      style={{
        padding: "20px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      }}
    >
      <h2 style={{ marginBottom: "10px", fontSize: "20px", color: "#1a1a1a" }}>
        Stock Movement
      </h2>

      <Form layout="vertical">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Source Store" required>
              <Select
                placeholder="Select Source Store"
                value={selectedStore?._id}
                onChange={(value) => {
                  const store = stores.find((store) => store._id === value);
                  setSelectedStore(store || null);
                  setProducts([]);
                  setSelectedProduct(null);
                  setTrackingNumbers([]);
                  setBatchNumbers([]);
                  setSelectedTrackingNo(null);
                  setSelectedBatchNo(null);
                }}
                style={{ width: "100%", borderRadius: "6px" }}
                loading={isStoreLoading}
                disabled={
                  isStoreLoading || isSubmitting || user?.role !== "admin"
                }
              >
                {stores.map((store) => (
                  <Option key={store._id} value={store._id}>
                    {store.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Select Product" required>
              <Select
                placeholder="Search and Select Product"
                value={selectedProduct?.product.id || undefined}
                onChange={(value) => {
                  const product = products.find(
                    (prod) => prod.product.id === value
                  );
                  setSelectedProduct(product || null);
                  setSelectedTrackingNo(null);
                  setSelectedBatchNo(null);
                }}
                onSearch={(input) => {
                  debouncedFetch(input, selectedStore?._id);
                }}
                showSearch
                filterOption={false}
                notFoundContent={
                  isProductLoading ? (
                    <div style={{ textAlign: "center", padding: "10px" }}>
                      <Spin size="small" /> Loading products...
                    </div>
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No products found"
                    />
                  )
                }
                loading={isProductLoading}
                disabled={isStoreLoading || isSubmitting || !selectedStore}
                style={{ width: "100%", borderRadius: "6px" }}
              >
                {products.map((product) => {
                  const optionText = `${
                    product.product.name || "Unnamed Product"
                  } ${product.barcode ? `(${product.barcode})` : ""}`;
                  return (
                    <Option
                      key={product.product.id}
                      value={product.product.id}
                      label={optionText}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>{optionText}</span>
                        <span>
                          <span>{`${formatNumber(product.salePrice)}`}</span>{" "}
                          <span>{`(In Stock: ${product.quantity || 0})`}</span>
                        </span>
                      </div>
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
          {selectedStore && storeSettings.trackingNumber === "Yes" ? (
            <Col xs={24} sm={6} md={4}>
              <Form.Item label="Select Tracking Number">
                <Select
                  placeholder="Select Tracking Number"
                  value={selectedTrackingNo}
                  onChange={(value) => setSelectedTrackingNo(value)}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={
                    trackingNumbers.length === 0 ? (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No tracking numbers available"
                      />
                    ) : null
                  }
                  style={{ width: "100%", borderRadius: "6px" }}
                  disabled={isSubmitting || trackingNumbers.length === 0}
                  allowClear
                >
                  {trackingNumbers.map((tracking) => (
                    <Option key={tracking} value={tracking}>
                      {tracking}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          ) : null}
          {selectedStore && storeSettings.batchNumber === "Yes" ? (
            <Col xs={24} sm={6} md={4}>
              <Form.Item label="Select Batch Number">
                <Select
                  placeholder="Select Batch Number"
                  value={selectedBatchNo}
                  onChange={(value) => setSelectedBatchNo(value)}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={
                    batchNumbers.length === 0 ? (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No batch numbers available"
                      />
                    ) : null
                  }
                  style={{ width: "100%", borderRadius: "6px" }}
                  disabled={isSubmitting || batchNumbers.length === 0}
                  allowClear
                >
                  {batchNumbers.map((batch) => (
                    <Option key={batch} value={batch}>
                      {batch}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          ) : null}
          <Col xs={24} sm={6} md={2}>
            <Form.Item label="Quantity" required>
              <Input
                type="number"
                min={1}
                value={quantity}
                onChange={(e) =>
                  setQuantity(Math.max(1, Number(e.target.value) || 1))
                }
                style={{ width: "100%", borderRadius: "6px" }}
                disabled={isSubmitting}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6} md={2}>
            <Form.Item label=" " colon={false}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddProduct}
                style={{ width: "100%", borderRadius: "6px" }}
                loading={isProductLoading}
                disabled={isProductLoading || isSubmitting || !selectedProduct}
              >
                ADD
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <div style={{ margin: "24px 0" }}>
        <Table
          dataSource={productTable}
          columns={columns}
          pagination={false}
          bordered
          rowKey="key"
          locale={{ emptyText: "No products added yet" }}
          style={{ borderRadius: "6px", overflow: "hidden" }}
          loading={isSubmitting}
        />
      </div>

      <Form layout="vertical">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Destination Store" required>
              <Select
                placeholder="Select Destination Store"
                value={selectedToStoreId}
                onChange={setSelectedToStoreId}
                style={{ width: "100%", borderRadius: "6px" }}
                loading={isStoreLoading}
                disabled={isStoreLoading || isSubmitting}
              >
                {stores
                  .filter((store) => store._id !== selectedStore?._id)
                  .map((store) => (
                    <Option key={store._id} value={store._id}>
                      {store.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Transfer Date" required>
              <DatePicker
                value={transferDate}
                onChange={(date) => setTransferDate(date)}
                style={{ width: "100%", borderRadius: "6px" }}
                placeholder="Select Date"
                disabled={isSubmitting}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label=" " colon={false}>
              <Button
                type="primary"
                onClick={handleSubmitTransfer}
                disabled={
                  !selectedToStoreId ||
                  !transferDate ||
                  productTable.length === 0 ||
                  isSubmitting
                }
                loading={isSubmitting}
                style={{ width: "100%", borderRadius: "6px" }}
              >
                Transfer Stock
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default StockMovementForm;
